import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Heart, Package, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cart, getCartCount, getCartTotal } = useCart();
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'About', path: '/about' },
    { name: 'Ingredients', path: '/ingredients' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const isCheckoutOrSuccess = location.pathname === '/checkout' || location.pathname === '/order-success';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className="bg-white border-b border-border sticky top-0 z-50" data-testid="main-navigation">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_seedoza-shop-1/artifacts/n1a0snf4_Logo%20of%20seedoza.png" 
                alt="Seedoza Logo" 
                className="h-14 md:h-24 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`text-base font-medium transition-colors hover:text-primary ${
                    location.pathname === item.path ? 'text-primary' : 'text-foreground'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Auth Buttons */}
              {isAuthenticated() ? (
                <>
                  <Link
                    to="/dashboard"
                    data-testid="dashboard-link"
                    className="flex items-center space-x-2 text-foreground hover:text-primary transition-colors"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-sm font-medium">{user?.name}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="flex items-center space-x-2 text-foreground hover:text-destructive transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="login-link"
                    className="text-base font-medium text-foreground hover:text-primary transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    data-testid="register-link"
                  >
                    <button className="bg-primary text-white px-6 py-2 rounded-full font-semibold hover:bg-primary/90 transition-colors">
                      Sign Up
                    </button>
                  </Link>
                </>
              )}
              
              <Link
                to="/cart"
                data-testid="cart-link"
                className="relative flex items-center space-x-2 text-foreground hover:text-primary transition-colors"
              >
                <ShoppingCart className="w-6 h-6" />
                {getCartCount() > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-2 -right-2 bg-accent text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  >
                    {getCartCount()}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-3">
              <Link to="/cart" className="relative p-2" data-testid="mobile-cart-link">
                <ShoppingCart className="w-6 h-6 text-foreground" />
                {getCartCount() > 0 && (
                  <span className="absolute top-0 right-0 bg-accent text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Link>
              <button
                data-testid="mobile-menu-button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      <div 
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menu Panel */}
        <div 
          className={`absolute top-0 right-0 h-full w-[85%] max-w-sm bg-white shadow-xl transform transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          data-testid="mobile-menu"
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <span className="font-heading font-bold text-lg">Menu</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 -mr-2"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* User Section */}
          {isAuthenticated() ? (
            <div className="p-4 bg-muted/30 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-muted/30 border-b border-border">
              <div className="flex gap-3">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-3 text-center border border-primary text-primary rounded-full font-semibold"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-3 text-center bg-primary text-white rounded-full font-semibold"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          )}

          {/* Navigation Links */}
          <div className="py-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center justify-between px-4 py-4 text-lg font-medium active:bg-muted/50 ${
                  location.pathname === item.path ? 'text-primary bg-primary/5' : 'text-foreground'
                }`}
              >
                <span>{item.name}</span>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </Link>
            ))}
          </div>

          {/* User Actions */}
          {isAuthenticated() && (
            <div className="border-t border-border">
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-4 py-4 text-lg font-medium active:bg-muted/50"
              >
                <Package className="w-5 h-5 mr-3 text-muted-foreground" />
                My Orders
              </Link>
              <Link
                to="/wishlist"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center px-4 py-4 text-lg font-medium active:bg-muted/50"
              >
                <Heart className="w-5 h-5 mr-3 text-muted-foreground" />
                Wishlist
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-4 text-lg font-medium text-red-600 active:bg-red-50"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile Bottom Cart Bar */}
      {getCartCount() > 0 && !isCheckoutOrSuccess && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-3 md:hidden z-40 shadow-[0_-4px_10px_rgba(0,0,0,0.1)]">
          <Link to="/cart" className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <ShoppingCart className="w-6 h-6 text-primary" />
                <span className="absolute -top-2 -right-2 bg-accent text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{getCartCount()} items</p>
                <p className="font-bold text-primary">₹{getCartTotal().toFixed(2)}</p>
              </div>
            </div>
            <button className="bg-primary text-white px-6 py-3 rounded-full font-semibold flex items-center">
              View Cart
              <ChevronRight className="w-5 h-5 ml-1" />
            </button>
          </Link>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground" data-testid="footer">
        <div className="container-custom py-10 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1 space-y-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_seedoza-shop-1/artifacts/n1a0snf4_Logo%20of%20seedoza.png" 
                alt="Seedoza Logo" 
                className="h-12 md:h-14 w-auto"
              />
              <p className="text-sm">Smart Energy Snacks Made From Super Seeds</p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-heading font-semibold text-base md:text-lg mb-3 md:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigation.slice(0, 4).map((item) => (
                  <li key={item.name}>
                    <Link to={item.path} className="text-sm hover:text-accent transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading font-semibold text-base md:text-lg mb-3 md:mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li>hello@seedoza.com</li>
                <li>Bangalore, India</li>
              </ul>
            </div>

            {/* Social */}
            <div className="col-span-2 md:col-span-1">
              <h3 className="font-heading font-semibold text-base md:text-lg mb-3 md:mb-4">Follow Us</h3>
              <a 
                href="https://www.instagram.com/seedoza?igsh=MWdya2UzNnlyOXhncA%3D%3D&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center space-x-2 text-sm hover:text-accent transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span>Instagram</span>
              </a>
            </div>
          </div>

          <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-secondary-foreground/20 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Seedoza. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
